import { useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useDarkTheme } from "@/libraries/design/utils/dark-theme-provider";

import { leftArrowPixelDot, rightArrowPixelDot } from "./pixel-dot-arrow-icons";

export type GalleryControlsSize = "small" | "large";

export type GalleryControlsProps = {
    showCounter?: boolean;
    currentCount?: number;
    totalCount?: number;
    onPrevious: () => void;
    onNext: () => void;
    isFirstItemVisible?: boolean;
    isLastItemVisible?: boolean;
    size?: GalleryControlsSize;
};

const arrowCss = css({
    flexShrink: 0
});

const containerCss = css({
    "display": "inline-flex",
    "flexDirection": "row",
    "alignItems": "center",
    "gap": token("space.100"),
    "--button-color": wacToken("color.primary.black"),
    "--hover-color": wacToken("color.secondary.navy"),
    "--active-color": wacToken("color.secondary.navy"),
    "--disabled-color": wacToken("color.primary.black"),
    "--focus-color": token("color.border.focused")
});

const darkThemeContainerCss = css({
    "--button-color": wacToken("color.neutral.light0"),
    "--hover-color": wacToken("color.extended.blue300"),
    "--active-color": wacToken("color.extended.blue300"),
    "--disabled-color": wacToken("color.neutral.light0"),
    "--focus-color": token("color.border.focused")
});

const buttonBaseCss = css({
    "display": "inline-flex",
    "cursor": "pointer",
    "flexShrink": 0,
    "border": "none",
    "borderRadius": "100%",
    "flexDirection": "column",
    "opacity": 1,
    "width": "40px",
    "height": "40px",
    "&:active:not(:disabled)": {
        transform: "scale(0.97)"
    },
    "&:disabled": {
        opacity: 0.3,
        cursor: "default"
    },
    "&:focus-visible:not(:disabled)": {
        outline: "2px solid var(--focus-color)",
        outlineOffset: "2px"
    }
});

const arrowButtonStyles = css({
    "alignItems": "center",
    "justifyContent": "center",
    "backgroundColor": "var(--button-color)",
    "color": wacToken("color.neutral.light0"),
    "padding": "13px 10px",
    "&:hover:not(:disabled)": {
        backgroundColor: "var(--hover-color)"
    },
    "&:active:not(:disabled)": {
        backgroundColor: "var(--active-color)"
    },
    "&:disabled": {
        backgroundColor: "var(--disabled-color) !important"
    }
});

const darkThemeButtonCss = css({
    "padding": 0,
    "backgroundColor": "transparent",
    "color": "var(--button-color)",
    "&:hover:not(:disabled)": {
        color: "var(--hover-color)"
    },
    "&:active:not(:disabled)": {
        color: "var(--active-color)"
    },
    "&:disabled": {
        color: "var(--disabled-color)"
    }
});

const buttonLargeCss = css({
    [media.above.md]: {
        width: "60px",
        height: "60px"
    }
});

const arrowButtonLargeCss = css({
    [media.above.md]: {
        padding: "19.5px 15px"
    }
});

const counterCss = css({
    minWidth: "40px",
    textAlign: "center",
    userSelect: "none",
    margin: 0
});

export const GalleryControls = ({
    showCounter = false,
    currentCount,
    totalCount,
    onPrevious,
    onNext,
    isFirstItemVisible,
    isLastItemVisible,
    size
}: // eslint-disable-next-line complexity
GalleryControlsProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const { isDarkTheme } = useDarkTheme();

    const fireGalleryControlClicked = useCallback(
        (direction: "previous" | "next") => {
            createAnalyticsEvent({
                action: "clicked",
                actionSubject: "galleryControlButton",
                eventType: "ui",
                eventComponent: "button",
                source: "gallery-controls",
                attributes: {
                    direction
                }
            }).fire();
        },
        [createAnalyticsEvent]
    );

    const handlePreviousClick = useCallback(() => {
        fireGalleryControlClicked("previous");
        onPrevious();
    }, [fireGalleryControlClicked, onPrevious]);

    const handleNextClick = useCallback(() => {
        fireGalleryControlClicked("next");
        onNext();
    }, [fireGalleryControlClicked, onNext]);

    const isLarge = size === "large";
    const counterTypographyClass = size === "small" ? "body-sm" : "body-md";
    const isAtFirstItem = currentCount !== undefined && currentCount < 2;
    const isAtLastItem =
        totalCount !== undefined &&
        currentCount !== undefined &&
        currentCount >= totalCount;

    return (
        <div
            css={[containerCss, isDarkTheme && darkThemeContainerCss]}
            role="group"
            aria-label={intl.formatMessage({
                id: "gallery-controls.group-label",
                defaultMessage: "Gallery navigation"
            })}
        >
            <button
                type="button"
                css={[
                    buttonBaseCss,
                    isDarkTheme ? darkThemeButtonCss : arrowButtonStyles,
                    isLarge && buttonLargeCss,
                    isLarge && !isDarkTheme && arrowButtonLargeCss
                ]}
                onClick={handlePreviousClick}
                disabled={isFirstItemVisible || isAtFirstItem}
                aria-label={intl.formatMessage({
                    id: "gallery-controls.previous",
                    defaultMessage: "Previous slide"
                })}
                data-testid="gallery-controls-previous"
            >
                {isDarkTheme ? (
                    leftArrowPixelDot
                ) : (
                    <svg
                        css={arrowCss}
                        viewBox="0 0 29 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden={true}
                        style={{ transform: "scaleX(-1)" }}
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M20.5361 19.1287C19.6575 20.0074 18.2322 20.0074 17.3535 19.1287C16.4748 18.25 16.4748 16.8248 17.3535 15.9461L21.248 12.0525L2.25 12.0525C1.00754 12.0523 0 11.045 0 9.80254C0.000234604 8.56023 1.00769 7.55276 2.25 7.55254L21.0654 7.55254L17.3535 3.84063C16.475 2.96202 16.4751 1.53765 17.3535 0.658987C18.2322 -0.219692 19.6575 -0.219692 20.5361 0.658987L27.8291 7.95293C28.4154 8.35896 28.8006 9.03537 28.8008 9.80254C28.8008 10.2822 28.6493 10.7265 28.3936 11.0916C28.2867 11.3125 28.1433 11.5206 27.96 11.7039L20.5361 19.1287Z"
                            fill="currentColor"
                        />
                    </svg>
                )}
            </button>
            {showCounter && (
                <p
                    css={counterCss}
                    className={counterTypographyClass}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    data-testid="gallery-controls-counter"
                    aria-label={intl.formatMessage(
                        {
                            id: "gallery-controls.counter",
                            defaultMessage: "{current} of {total} slide"
                        },
                        {
                            current: currentCount,
                            total: totalCount
                        }
                    )}
                >
                    {currentCount}/{totalCount}
                </p>
            )}
            <button
                type="button"
                css={[
                    buttonBaseCss,
                    isDarkTheme ? darkThemeButtonCss : arrowButtonStyles,
                    isLarge && buttonLargeCss,
                    isLarge && !isDarkTheme && arrowButtonLargeCss
                ]}
                onClick={handleNextClick}
                disabled={isLastItemVisible || isAtLastItem}
                aria-label={intl.formatMessage({
                    id: "gallery-controls.next",
                    defaultMessage: "Next slide"
                })}
                data-testid="gallery-controls-next"
            >
                {isDarkTheme ? (
                    rightArrowPixelDot
                ) : (
                    <svg
                        css={arrowCss}
                        viewBox="0 0 29 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden={true}
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M20.5361 19.1287C19.6575 20.0074 18.2322 20.0074 17.3535 19.1287C16.4748 18.25 16.4748 16.8248 17.3535 15.9461L21.248 12.0525L2.25 12.0525C1.00754 12.0523 0 11.045 0 9.80254C0.000234604 8.56023 1.00769 7.55276 2.25 7.55254L21.0654 7.55254L17.3535 3.84063C16.475 2.96202 16.4751 1.53765 17.3535 0.658987C18.2322 -0.219692 19.6575 -0.219692 20.5361 0.658987L27.8291 7.95293C28.4154 8.35896 28.8006 9.03537 28.8008 9.80254C28.8008 10.2822 28.6493 10.7265 28.3936 11.0916C28.2867 11.3125 28.1433 11.5206 27.96 11.7039L20.5361 19.1287Z"
                            fill="currentColor"
                        />
                    </svg>
                )}
            </button>
        </div>
    );
};
